import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Medal, Trophy, Upload, EyeOff, Eye, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);

function periodOptions() {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth(), 1 - i * 15);
    const p = d.getDate() <= 15 ? 1 : 2;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${p}`;
    if (!out.includes(key)) out.push(key);
  }
  const n = new Date();
  const cur = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${n.getDate() <= 15 ? 1 : 2}`;
  return Array.from(new Set([cur, ...out]));
}

export default function StarTeacherPanel() {
  const [period, setPeriod] = useState(periodOptions()[0]);
  const [report, setReport] = useState(null);
  const [awards, setAwards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teacherId, setTeacherId] = useState("");
  const [photo, setPhoto] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([api.get("/admin/recognition/report", { params: { period } }), api.get("/admin/recognition/awards")]);
      setReport(r.data); setAwards(a.data);
      setTeacherId(r.data.recommended_teacher_id || (r.data.rows[0]?.teacher_id ?? ""));
    } catch { toast.error("Could not load recognition data"); }
    finally { setLoading(false); }
  }, [period]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setPhoto(data.url); toast.success("Photo uploaded"); }
    catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };
  const publish = async () => {
    if (!consent) { toast.error("Please confirm the teacher's consent"); return; }
    setPublishing(true);
    try { await api.post("/admin/recognition/publish", { period_key: period, teacher_id: teacherId, photo_url: photo, message, consent }); toast.success("Star Teacher published to the website"); setPhoto(""); setMessage(""); setConsent(false); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to publish"); }
    finally { setPublishing(false); }
  };
  const archive = async (id, on) => { try { await api.post(`/admin/recognition/awards/${id}/${on ? "archive" : "unarchive"}`); toast.success(on ? "Removed from public display" : "Restored to public display"); load(); } catch { toast.error("Failed"); } };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blue-900" /></div>;

  return (
    <div data-testid="panel-starteacher">
      <h2 className="text-2xl font-bold text-slate-900 mb-1">PTG Star Teacher — 15 Day Award</h2>
      <p className="text-sm text-slate-500 mb-5">Recognition for responsible, consistent and meaningful use of the Student Star System. This is <b>not</b> based on who gave the most stars, and attendance does not affect it. Nothing is published until you approve it with the teacher's consent.</p>

      <div className="flex items-center gap-3 mb-4">
        <Label className="text-sm font-semibold">Period</Label>
        <Select value={period} onValueChange={setPeriod}><SelectTrigger className="w-64" data-testid="period-select"><SelectValue /></SelectTrigger>
          <SelectContent>{periodOptions().map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
        {report && <span className="text-sm text-slate-500">{report.period_label}</span>}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 mb-6 overflow-x-auto" data-testid="recognition-report">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-slate-500 border-b"><th className="py-2 pr-3">Teacher</th><th className="pr-3">Score</th><th className="pr-3">Eligible days</th><th className="pr-3">Days used</th><th className="pr-3">Students updated</th><th className="pr-3">Valid reasons</th><th>Warnings</th></tr></thead>
          <tbody>
            {report.rows.map(r => (
              <tr key={r.teacher_id} className={`border-b last:border-0 ${r.teacher_id === report.recommended_teacher_id ? "bg-amber-50" : ""}`} data-testid={`recog-row-${r.teacher_id}`}>
                <td className="py-2 pr-3 font-semibold">{r.teacher_name} {r.teacher_id === report.recommended_teacher_id && <Badge className="ml-1 bg-amber-500"><Trophy className="h-3 w-3 mr-1" />Recommended</Badge>}</td>
                <td className="pr-3 font-bold text-blue-900">{r.score}</td><td className="pr-3">{r.eligible_days}</td><td className="pr-3">{r.days_used}</td><td className="pr-3">{r.students_updated}/{r.assigned}</td><td className="pr-3">{r.valid_reason_txns}</td><td className={r.warnings > 0 ? "text-amber-600 font-semibold" : ""}>{r.warnings}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 mb-6 max-w-xl space-y-3" data-testid="publish-form">
        <p className="font-semibold text-slate-800 flex items-center gap-2"><Medal className="h-4 w-4 text-amber-500" /> Review & publish</p>
        <div><Label className="mb-1 block text-sm font-semibold">Winner</Label>
          <Select value={teacherId} onValueChange={setTeacherId}><SelectTrigger data-testid="winner-select"><SelectValue /></SelectTrigger>
            <SelectContent>{report.rows.map(r => <SelectItem key={r.teacher_id} value={r.teacher_id}>{r.teacher_name}</SelectItem>)}</SelectContent></Select></div>
        <div><Label className="mb-1 block text-sm font-semibold">Photograph</Label>
          <div className="flex items-center gap-3">
            {photo && <img src={imgUrl(photo)} alt="teacher" className="h-14 w-14 rounded-full object-cover" />}
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 cursor-pointer text-sm font-semibold hover:bg-slate-50" data-testid="photo-upload-label">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload photo
              <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="photo-input" />
            </label>
          </div>
        </div>
        <div><Label className="mb-1 block text-sm font-semibold">Appreciation message (optional)</Label><Textarea rows={2} value={message} onChange={e => setMessage(e.target.value)} data-testid="appreciation-message" /></div>
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
          <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} className="h-4 w-4 accent-blue-900" data-testid="consent-checkbox" />
          I confirm the teacher's permission/consent has been obtained to display their name and photo publicly.
        </label>
        <Button onClick={publish} disabled={publishing || !consent || !teacherId} className="bg-blue-900 hover:bg-blue-950" data-testid="publish-award">{publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve & Publish</>}</Button>
      </div>

      <p className="font-semibold text-slate-800 mb-2">Published & archived awards</p>
      {awards.length === 0 ? <p className="text-sm text-slate-500">No awards yet.</p> : (
        <div className="space-y-2" data-testid="awards-list">
          {awards.map(a => (
            <div key={a.id} className="rounded-lg border border-slate-200 bg-white p-3 flex items-center gap-3" data-testid={`award-${a.id}`}>
              {a.photo_url ? <img src={imgUrl(a.photo_url)} alt={a.teacher_name} className="h-10 w-10 rounded-full object-cover" /> : <div className="h-10 w-10 rounded-full bg-blue-900 text-white flex items-center justify-center font-bold">{a.teacher_name?.charAt(0)}</div>}
              <div className="flex-1 min-w-0"><p className="font-semibold text-slate-900">{a.teacher_name} <Badge variant="outline" className={a.status === "archived" ? "text-slate-500" : "text-green-600 border-green-300"}>{a.status}</Badge></p><p className="text-xs text-slate-500">{a.period_label}</p></div>
              <Button size="sm" variant="outline" onClick={() => archive(a.id, a.status !== "archived")} data-testid={`archive-${a.id}`}>{a.status === "archived" ? <><Eye className="h-4 w-4 mr-1" />Show</> : <><EyeOff className="h-4 w-4 mr-1" />Hide</>}</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
